import axios from '@/src/api/axios_init'
import type { Building } from '@/src/api/types/backend/locations-info.api'
import { createContext, useEffect, useState } from 'react'
import { EnergyTypeEnum } from '../enums/energyType.enum'
import { useLocationsContext } from './LocationsContext'

export const InsightsContext = createContext<any>(null)

export interface IGraphMarker {
  datepickerFrom: string
  datepickerTo: string
  from: string
  to: string
}

export interface IInsight {
  id: string
  approved: boolean
  category: string
  createdAt: string
  graphMarker: IGraphMarker[]
  impact: string
  irrelevantCount: number
  isCustom: boolean
  kpi: string
  seenAt: string
  subtype: string
  tabIndex: number
  text: string
  type: string
  visible: boolean
  buildingName: string | null
  buildingIndex: number | null
  locationName: string | null
  locationIndex: number | null
  deviceId: string
  locationId: number | null
}

const fetchInsights = async ({ deviceId, buildingName, buildingIndex, locationName, locationIndex, locationId }) => {
  if (!deviceId) return []

  const res = await axios.get<IInsight[]>('/functions/insights', { params: { deviceId } })
  return res.data.map(insight => ({
    ...insight,
    buildingName,
    buildingIndex,
    locationName,
    locationIndex,
    locationId,
  }))
}

export const InsightsProvider = ({ children }) => {
  const { getDevices, currentLocation, selectedBuildingIndex, selectedLocationIndex, locations } = useLocationsContext()
  const [insights, setInsights] = useState<any>(null)

  const findMainElectricityDeviceId = (deviceIds?: string[]): string | undefined => {
    if (!deviceIds) return undefined

    const devices = getDevices()
    if (!devices || !Array.isArray(devices)) return undefined

    const electricityMainDevice = devices.find(
      d => d?.isMainSource && d?.energyType === EnergyTypeEnum.ELECTRICITY && deviceIds.includes(d?.id)
    )
    return electricityMainDevice?.id
  }

  useEffect(() => {
    if (currentLocation && selectedBuildingIndex > -1) {
      const fetchParams =
        selectedBuildingIndex > 0 && selectedLocationIndex > 0
          ? [
              {
                deviceId: findMainElectricityDeviceId((currentLocation.buildings[selectedBuildingIndex] as Building).deviceIds),
                buildingName: currentLocation.buildings[selectedBuildingIndex].name,
                buildingIndex: null,
                locationName: null,
                locationIndex: null,
                locationId: currentLocation.locationPgId,
              },
            ]
          : currentLocation.buildings.slice(1).map(building => {
              const locationOfBuilding = locations.slice(1).find(location => location.buildings.some(bld => bld.id === building.id))
              const indexOfBuilding = locationOfBuilding?.buildings.findIndex(bld => bld.id === building.id)

              return {
                deviceId: findMainElectricityDeviceId((building as Building).deviceIds),
                buildingName: building.name,
                buildingIndex: indexOfBuilding,
                locationName: locationOfBuilding?.name,
                locationIndex: locationOfBuilding?.index,
                locationId: locationOfBuilding?.locationPgId,
              }
            })

      const insightsGroup = fetchParams.filter(param => !!param.deviceId).map(fetchInsights)

      Promise.all(insightsGroup)
        .then(insightsArr => {
          const orderedInsights = insightsArr.flat().sort((a, b) => b.createdAt.localeCompare(a.createdAt))
          setInsights(orderedInsights)
        })
        .catch(err => console.error(err))
    }
  }, [currentLocation, selectedBuildingIndex])

  return <InsightsContext.Provider value={insights}>{children}</InsightsContext.Provider>
}
